import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { PASSWORD_CHANGE_SUCCESS_MESSAGE } from "@/constants/common";
import { resetPasswordSchema } from "@/features/auth/schema/resetPasswordSchema";
import { resetPasswordApi } from "@/features/auth/services/resetPassword";
import { TResetPasswordForm } from "@/features/auth/types/resetPasswordForm";
import { TResetPasswordQuery } from "@/features/auth/types/resetPasswordQuery";
import { useToast } from "@/hooks/useToast";
import { AppRoutes } from "@/routes/config";
import { setErrorForm, showError } from "@/utils/error";
import { getQueryFromURL } from "@/utils/url";
import { useNavigate } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import { Paper, Stack, Typography, styled } from "@mui/material";
import { useForm } from "react-hook-form";

const initForm: TResetPasswordForm = {
  email: "",
  newPassword: "",
  confirmNewPassword: "",
};

export const ResetPasswordForm = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const query = getQueryFromURL<TResetPasswordQuery>();
  const methods = useForm<TResetPasswordForm>({
    mode: "all",
    reValidateMode: "onBlur",
    defaultValues: { ...initForm, email: query?.email ?? "" },
    resolver: yupResolver(resetPasswordSchema),
  });
  const { control } = methods;

  const onSubmit = async (data: TResetPasswordForm) => {
    const { newPassword } = data;
    if (!query) return;
    const { email, resetToken } = query;
    try {
      if (email && resetToken) {
        await resetPasswordApi({ email, newPassword, resetToken });
        toast.success(PASSWORD_CHANGE_SUCCESS_MESSAGE);
        navigate(AppRoutes.resetPasswordSuccess, { state: { email } });
      }
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
  };

  return (
    <Container
      elevation={0}
      sx={{
        width: 464,
        p: 4,
        borderRadius: "16px",
      }}
    >
      <Stack gap={3}>
        <Typography variant="head20Bold" textAlign="center">
          パスワード再発行
        </Typography>
        <Typography fontSize="14px" color="text.secondary" mt={1} mb="12px">
          新しいパスワードを入力してください。
        </Typography>
        <Form methods={methods} onSubmit={onSubmit} enableBlocker={false} schema={resetPasswordSchema}>
          <Stack gap={2.5}>
            <FormField label="メールアドレス">
              <Input type="text" size="lg" readonly nonControl value={query?.email ?? ""} />
            </FormField>
            <FormField control={control} name="newPassword" label="新しいパスワード" required>
              <Input type="password" size="lg" />
            </FormField>
            <FormField control={control} name="confirmNewPassword" label="新しいパスワード（確認用）" required>
              <Input type="password" size="lg" />
            </FormField>
            <Typography fontSize="13px" color="text.secondary">
              ※パスワードはアルファベット大文字・小文字・数字・記号を含む12文字以上で入力してください。
            </Typography>
            <Button type="submit" variant="primary" fullWidth size="sm" sx={{ mt: "12px" }}>
              パスワードを再発行
            </Button>
          </Stack>
        </Form>
      </Stack>
    </Container>
  );
};

const Container = styled(Paper)`
  box-shadow:
    0 4px 4px -1px rgba(12, 12, 13, 0.05),
    0 4px 4px -1px rgba(12, 12, 13, 0.1) !important;
`;
